import {Checkbox, Chip, NumberInput, Textarea, Tooltip, Card} from 'platform/components';
import {GridItem, HStack, Icon, Show, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {SaveSaleVehicleLeasingBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {COUNTRY_DEU, useTenant} from '@dms/shared';

import {LeasingIncludableCost} from './LeasingIncludableCost';
import {LeasingParameterSection} from './LeasingParameterSection';
import {MoneyInputField} from './MoneyInputField';
import {createLeasingDownPaymentFromPercentage} from './utils/leasingBody/createLeasingDownPaymentFromPercentage';

const SPECIAL_CONDITIONS: ReadonlyArray<{code: string; labelKey: string}> = [
  {code: 'TRADE_IN', labelKey: 'tradeInBonus'},
  {code: 'DISABILITY_DISCOUNT', labelKey: 'disabilityDiscount'},
  {code: 'LOYALTY_BONUS', labelKey: 'loyaltyBonus'},
  {code: 'SWITCHER_BONUS', labelKey: 'switcherBonus'},
  {code: 'EV_BONUS', labelKey: 'evBonus'},
];

interface LeasingCommonParametersProps {
  vehicleLeasingBody: SaveSaleVehicleLeasingBody;
  currency: string;
  isReadOnly: boolean;
  onChange: (vehicleLeasingBody: SaveSaleVehicleLeasingBody) => void;
}

export function LeasingCommonParameters(props: LeasingCommonParametersProps) {
  const {isTenantCountrySupported} = useTenant();
  const isGermanTenant = isTenantCountrySupported(COUNTRY_DEU);
  const listPriceAmount = Number(props.vehicleLeasingBody.commonParameters.listPrice?.amount ?? 0);
  const downPaymentAmount = Number(
    props.vehicleLeasingBody.commonParameters.downPayment?.amount ?? 0
  );
  const downPaymentPercentage =
    listPriceAmount > 0 ? Math.round((downPaymentAmount / listPriceAmount) * 10_000) / 100 : null;
  const handleCommonParametersChange = (
    changes: Partial<SaveSaleVehicleLeasingBody['commonParameters']>
  ) =>
    props.onChange({
      ...props.vehicleLeasingBody,
      commonParameters: {...props.vehicleLeasingBody.commonParameters, ...changes},
    });

  return (
    <Card
      title={i18n.t('page.vehicle.financingAndLeasing.commonLeasingParameters')}
      titleIcon="action/info_outline"
      subtitle={i18n.t('page.vehicle.financingAndLeasing.commonLeasingParametersHint')}
      variant="inlineGrey"
    >
      <VStack spacing={6}>
        <LeasingParameterSection title={i18n.t('page.vehicle.financingAndLeasing.keyFigures')}>
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.listPrice')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.listPrice')}
            value={props.vehicleLeasingBody.commonParameters.listPrice}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(listPrice) => handleCommonParametersChange({listPrice})}
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.leasingDownPayment')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.leasingDownPayment')}
            value={props.vehicleLeasingBody.commonParameters.downPayment}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(downPayment) => handleCommonParametersChange({downPayment})}
          />
          <NumberInput
            label={i18n.t('page.vehicle.financingAndLeasing.downPaymentPercentage')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.leasingDownPayment')}
            value={downPaymentPercentage}
            suffix="%"
            decimalPlaces={2}
            minStepperValue={0}
            maxStepperValue={100}
            isDisabled={props.isReadOnly || listPriceAmount <= 0}
            onChange={(percentage) => {
              if (percentage !== null && (percentage < 0 || percentage > 100)) {
                return;
              }

              handleCommonParametersChange({
                downPayment:
                  percentage === null
                    ? null
                    : createLeasingDownPaymentFromPercentage({
                        listPrice: props.vehicleLeasingBody.commonParameters.listPrice,
                        fallbackCurrency: props.currency,
                        percentage,
                      }),
              });
            }}
          />
        </LeasingParameterSection>

        <LeasingParameterSection title={i18n.t('page.vehicle.financingAndLeasing.mileageHandling')}>
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.excessMileageCost')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.excessMileageCost')}
            value={props.vehicleLeasingBody.commonParameters.excessMileageCost}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(excessMileageCost) => handleCommonParametersChange({excessMileageCost})}
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.underMileageCredit')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.underMileageCredit')}
            value={props.vehicleLeasingBody.commonParameters.underMileageCredit}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(underMileageCredit) => handleCommonParametersChange({underMileageCredit})}
          />
        </LeasingParameterSection>

        <Show when={isGermanTenant}>
          <LeasingParameterSection title={i18n.t('page.vehicle.financingAndLeasing.oneTimeCosts')}>
            <LeasingIncludableCost
              label={i18n.t('page.vehicle.financingAndLeasing.vehicleTransfer')}
              included={props.vehicleLeasingBody.commonParameters.vehicleTransferIncluded}
              amount={props.vehicleLeasingBody.commonParameters.vehicleTransferAmount}
              currency={props.currency}
              isReadOnly={props.isReadOnly}
              onIncludedChange={(vehicleTransferIncluded) =>
                handleCommonParametersChange({vehicleTransferIncluded})
              }
              onAmountChange={(vehicleTransferAmount) =>
                handleCommonParametersChange({vehicleTransferAmount})
              }
            />
            <LeasingIncludableCost
              label={i18n.t('page.vehicle.financingAndLeasing.vehicleRegistration')}
              included={props.vehicleLeasingBody.commonParameters.vehicleRegistrationIncluded}
              amount={props.vehicleLeasingBody.commonParameters.vehicleRegistrationAmount}
              currency={props.currency}
              isReadOnly={props.isReadOnly}
              onIncludedChange={(vehicleRegistrationIncluded) =>
                handleCommonParametersChange({vehicleRegistrationIncluded})
              }
              onAmountChange={(vehicleRegistrationAmount) =>
                handleCommonParametersChange({vehicleRegistrationAmount})
              }
            />
            <LeasingIncludableCost
              label={i18n.t('page.vehicle.financingAndLeasing.vehicleDelivery')}
              included={props.vehicleLeasingBody.commonParameters.vehicleDeliveryIncluded}
              amount={props.vehicleLeasingBody.commonParameters.vehicleDeliveryAmount}
              currency={props.currency}
              isReadOnly={props.isReadOnly}
              onIncludedChange={(vehicleDeliveryIncluded) =>
                handleCommonParametersChange({vehicleDeliveryIncluded})
              }
              onAmountChange={(vehicleDeliveryAmount) =>
                handleCommonParametersChange({vehicleDeliveryAmount})
              }
            />
          </LeasingParameterSection>
        </Show>

        <LeasingParameterSection title={i18n.t('page.vehicle.financingAndLeasing.monthlyServices')}>
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.maintenanceMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.maintenanceMonthly')}
            value={props.vehicleLeasingBody.commonParameters.maintenanceMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(maintenanceMonthly) => handleCommonParametersChange({maintenanceMonthly})}
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.wearMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.wearMonthly')}
            value={props.vehicleLeasingBody.commonParameters.wearMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(wearMonthly) => handleCommonParametersChange({wearMonthly})}
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.maintenanceAndWearMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.maintenanceAndWearMonthly')}
            value={props.vehicleLeasingBody.commonParameters.maintenanceAndWearMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(maintenanceAndWearMonthly) =>
              handleCommonParametersChange({maintenanceAndWearMonthly})
            }
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.vehicleTaxMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.vehicleTaxMonthly')}
            value={props.vehicleLeasingBody.commonParameters.vehicleTaxMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(vehicleTaxMonthly) => handleCommonParametersChange({vehicleTaxMonthly})}
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.liabilityMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.liabilityMonthly')}
            value={props.vehicleLeasingBody.commonParameters.liabilityInsuranceMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(liabilityInsuranceMonthly) =>
              handleCommonParametersChange({liabilityInsuranceMonthly})
            }
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.liabilityDeductible')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.liabilityDeductible')}
            value={props.vehicleLeasingBody.commonParameters.liabilityInsuranceDeductible}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(liabilityInsuranceDeductible) =>
              handleCommonParametersChange({liabilityInsuranceDeductible})
            }
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.comprehensiveMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.comprehensiveMonthly')}
            value={props.vehicleLeasingBody.commonParameters.comprehensiveInsuranceMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(comprehensiveInsuranceMonthly) =>
              handleCommonParametersChange({comprehensiveInsuranceMonthly})
            }
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.comprehensiveDeductible')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.comprehensiveDeductible')}
            value={props.vehicleLeasingBody.commonParameters.comprehensiveInsuranceDeductible}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(comprehensiveInsuranceDeductible) =>
              handleCommonParametersChange({comprehensiveInsuranceDeductible})
            }
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.tiresMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.tiresMonthly')}
            value={props.vehicleLeasingBody.commonParameters.tiresMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(tiresMonthly) => handleCommonParametersChange({tiresMonthly})}
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.inspectionMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.inspectionMonthly')}
            value={props.vehicleLeasingBody.commonParameters.inspectionMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(inspectionMonthly) => handleCommonParametersChange({inspectionMonthly})}
          />
          <MoneyInputField
            label={i18n.t('page.vehicle.financingAndLeasing.returnValueInsuranceMonthly')}
            tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.returnValueInsuranceMonthly')}
            value={props.vehicleLeasingBody.commonParameters.returnValueInsuranceMonthly}
            currency={props.currency}
            isDisabled={props.isReadOnly}
            onChange={(returnValueInsuranceMonthly) =>
              handleCommonParametersChange({returnValueInsuranceMonthly})
            }
          />
        </LeasingParameterSection>

        <Show when={isGermanTenant}>
          <LeasingParameterSection
            title={i18n.t('page.vehicle.financingAndLeasing.specialConditions')}
          >
            <GridItem span={[1, 2, 4]}>
              <HStack spacing={2} wrap>
                {Array.from(
                  new Set([
                    ...SPECIAL_CONDITIONS.map((condition) => condition.code),
                    ...props.vehicleLeasingBody.commonParameters.specialConditionCodes,
                  ])
                ).map((conditionCode) => {
                  const condition = SPECIAL_CONDITIONS.find(
                    (candidate) => candidate.code === conditionCode
                  );
                  const label = match(condition)
                    .with(Pattern.nonNullable, (knownCondition) =>
                      i18n.t(`page.vehicle.financingAndLeasing.${knownCondition.labelKey}`)
                    )
                    .otherwise(() => conditionCode);
                  const isSelected =
                    props.vehicleLeasingBody.commonParameters.specialConditionCodes.includes(
                      conditionCode
                    );

                  return (
                    <Chip
                      key={conditionCode}
                      label={label}
                      value={isSelected}
                      isDisabled={props.isReadOnly}
                      onChange={() =>
                        handleCommonParametersChange({
                          specialConditionCodes: isSelected
                            ? props.vehicleLeasingBody.commonParameters.specialConditionCodes.filter(
                                (code) => code !== conditionCode
                              )
                            : [
                                ...props.vehicleLeasingBody.commonParameters.specialConditionCodes,
                                conditionCode,
                              ],
                        })
                      }
                    />
                  );
                })}
              </HStack>
            </GridItem>
          </LeasingParameterSection>
        </Show>

        <LeasingParameterSection title={i18n.t('page.vehicle.financingAndLeasing.notesAndTradeIn')}>
          <HStack align="center">
            <Checkbox
              label={i18n.t('page.vehicle.financingAndLeasing.tradeInPossible')}
              value={props.vehicleLeasingBody.commonParameters.tradeInPossible}
              isDisabled={props.isReadOnly}
              onChange={(tradeInPossible) => handleCommonParametersChange({tradeInPossible})}
            />
            <Tooltip description={i18n.t('page.vehicle.financingAndLeasing.hints.tradeInPossible')}>
              <Icon value="action/help" color="text.secondary" size={4} tabIndex={0} />
            </Tooltip>
          </HStack>
          <GridItem span={[1, 2, 4]}>
            <Textarea
              label={i18n.t('page.vehicle.financingAndLeasing.internalNotes')}
              placeholder={i18n.t('page.vehicle.financingAndLeasing.internalNotesPlaceholder')}
              value={props.vehicleLeasingBody.commonParameters.internalNotes ?? ''}
              minRows={3}
              isDisabled={props.isReadOnly}
              onChange={(internalNotes) =>
                handleCommonParametersChange({
                  internalNotes: internalNotes === '' ? null : internalNotes,
                })
              }
            />
          </GridItem>
        </LeasingParameterSection>
      </VStack>
    </Card>
  );
}
